import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "convex/_generated/api";
import type { Id } from "convex/_generated/dataModel";
import { useApiMutation } from "@/packages/features-shared";
import { useTranslation } from "@/packages/utils";
import { Archive, InboxIcon } from "lucide-react";
import { springs } from "../ui/motion";
import { NotificationDetailHeader } from "./NotificationDetailHeader";
import { RelatedEntityCard } from "./RelatedEntityCard";
import { getRenderer } from "./renderers";
import { getNotificationConfig } from "./notificationConfig";
import type { NotificationDetail } from "./types";

interface Props {
  notificationId: string;
  isArchived: boolean;
  onArchive: () => void;
  onUnarchive: () => void;
}

export function InboxNotificationDetail({
  notificationId,
  isArchived,
  onArchive,
  onUnarchive,
}: Props) {
  const { t } = useTranslation("inbox");
  const markedRef = useRef<string | null>(null);

  const { data: notification, isLoading } = useQuery(
    convexQuery(api.notifications.queries.index.getById, {
      id: notificationId as Id<"notifications">,
    }),
  );

  const markAsReadMutation = useApiMutation(
    api.notifications.mutations.index.markAsRead,
    {
      name: "mark-notification-as-read",
      silentSuccess: true,
    },
  );

  useEffect(() => {
    if (
      notification &&
      !notification.isRead &&
      markedRef.current !== notification.id
    ) {
      markedRef.current = notification.id;
      markAsReadMutation.mutate({
        notificationId: notification.id,
      });
    }
  }, [notification, markAsReadMutation.mutate]);

  if (isLoading) {
    return <DetailSkeleton />;
  }

  if (!notification) {
    return (
      <div className="flex items-center justify-center h-full bg-card">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-card-hover flex items-center justify-center">
            <InboxIcon size={18} className="text-muted" />
          </div>
          <p className="text-[13px] text-secondary">{t("notFound")}</p>
        </div>
      </div>
    );
  }

  const config = getNotificationConfig(notification.category);
  const renderer = getRenderer(notification.category);

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={notificationId}
        className="flex flex-col h-full bg-card overflow-y-auto scrollbar-thin"
        style={
          {
            "--detail-accent": config.color,
            "--detail-accent-soft": config.bgColor,
          } as React.CSSProperties
        }
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0, transition: { duration: 0.12 } }}
        transition={springs.smooth}
      >
        {/* ── Tinted header zone ─────────────────────────────── */}
        <div
          className="relative px-6 pt-6 pb-5"
          style={{
            background: `linear-gradient(180deg, ${config.color}0A 0%, ${config.color}03 70%, transparent 100%)`,
          }}
        >
          <NotificationDetailHeader
            category={notification.category}
            title={notification.title}
            createdAt={notification.createdAt}
            relatedEntityName={notification.relatedEntityName}
          />
        </div>

        {/* Header separator */}
        <motion.div
          className="mx-6 h-px"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ ...springs.smooth, delay: 0.18 }}
          style={{
            background: `linear-gradient(to right, ${config.color}20, var(--color-border) 40%, transparent 90%)`,
            transformOrigin: "left",
          }}
        />

        {/* ── Content zone ───────────────────────────────────── */}
        <div className="flex-1 flex flex-col px-6 pt-5 pb-6">
          {/* Renderer content (status widget, body, metadata) */}
          {renderer.render(notification as NotificationDetail)}

          {/* Related entity */}
          {notification.relatedEntityId && (
            <>
              <div className="h-px bg-border/40 mb-4" />
              <RelatedEntityCard
                relatedEntityId={notification.relatedEntityId}
                relatedEntityType={notification.relatedEntityType}
                relatedEntityName={notification.relatedEntityName}
                metadata={notification.metadata}
                targetAuditId={notification.targetAuditId}
              />
            </>
          )}

          {/* ── Action footer ──────────────────────────────── */}
          <div className="mt-auto pt-5">
            <div className="h-px bg-border/30 mb-4" />
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...springs.smooth, delay: 0.32 }}
            >
              {isArchived ? (
                <ActionButton
                  onClick={onUnarchive}
                  icon={<InboxIcon size={14} />}
                  label={t("moveToInbox")}
                />
              ) : (
                <ActionButton
                  onClick={onArchive}
                  icon={<Archive size={14} />}
                  label={t("archive")}
                />
              )}
            </motion.div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

function ActionButton({
  onClick,
  icon,
  label,
}: {
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      className="group relative inline-flex items-center gap-2 px-4 py-2 rounded-button text-[13px] font-medium text-secondary border border-border/60 bg-card overflow-hidden"
      whileHover={{ scale: 1.02, y: -1 }}
      whileTap={{ scale: 0.97 }}
      transition={springs.snappy}
    >
      <span className="absolute inset-0 bg-card-hover opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
      <span className="relative flex items-center gap-2">
        <span className="text-muted group-hover:text-secondary transition-colors duration-200">
          {icon}
        </span>
        {label}
      </span>
    </motion.button>
  );
}

function DetailSkeleton() {
  return (
    <div className="h-full bg-card flex flex-col">
      {/* Header skeleton */}
      <div className="px-6 pt-6 pb-5 bg-card-hover/30">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-full bg-card-hover shimmer" />
          <div className="flex-1 space-y-2.5 pt-1">
            <div className="h-[18px] bg-card-hover rounded-md w-3/4 shimmer" />
            <div className="h-3.5 bg-card-hover rounded-md w-2/5 shimmer" />
          </div>
        </div>
      </div>
      <div className="mx-6 h-px bg-border/30" />
      {/* Content skeleton */}
      <div className="px-6 pt-5 space-y-4">
        <div className="space-y-2.5">
          <div className="h-4 bg-card-hover rounded w-full shimmer" />
          <div className="h-4 bg-card-hover rounded w-5/6 shimmer" />
          <div className="h-4 bg-card-hover rounded w-3/5 shimmer" />
        </div>
        <div className="flex gap-2 pt-1">
          <div className="h-6 bg-card-hover rounded-full w-20 shimmer" />
          <div className="h-6 bg-card-hover rounded-full w-24 shimmer" />
        </div>
      </div>
    </div>
  );
}
